import React, { memo } from "react";
import cn from "classnames";

import warningTriangle from "@src/assets/svg/warning-triangle-red.svg";
import { OurNode } from "@src/cloud-connect/poc/our-node";
import styles from "./alarm-list-node.module.scss";
import { ToggleButton } from "@src/components/atoms/toggle-button/toggle-button";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { AlarmRow } from "@src/cloud-connect/alarms/entities/alarm-row";
import { useAlarmListNodeHook } from "./use-alarm-list-node-hook";
import {
  ToolTip,
  ToolTipPositionType,
} from "@src/components/atoms/tooltip/tooltip";
import { QuestionMarkWithPopoverForConditionMonitoring } from "@src/components/organisms/question-mark-with-popover-for-condition-monitoring/question-mark-with-popover-for-condition-monitoring";
import { RenderOnlyForDevelopers } from "@src/components/molecules/render-only-for-developers/render-only-for-developers";
import { useI18N } from "@src/language/language-context";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";

type TreeProps = {
  node: OurNode;
  alarmRow: AlarmRow;
  index: number;
  length: number;
};

const cssClassName = "alarm-list-node";

export const AlarmListNode = memo(
  ({ node, alarmRow, index, length }: TreeProps) => {
    const firstNumericInputRef = React.useRef<HTMLInputElement>(null);
    const secondNumericInputRef = React.useRef<HTMLInputElement>(null);
    const i18n = useI18N();
    const {
      isAlarmActive,
      firstOperator,
      secondOperator,
      firstCondition,
      secondCondition,
      returnUnit,
      renderCheckBoxButton,
      handleClickOnNode,
      returnIcon,
      buildAncestorBreadCrumbs,
      handleFirstMatchOnClick,
      handleFirstConditionOnBlur,
      handleSecondMatchOnClick,
      handleSecondConditionOnBlur,
      toggleHandleClick,
      setFirstCondition,
      setSecondCondition,
    } = useAlarmListNodeHook(
      node,
      alarmRow,
      firstNumericInputRef,
      secondNumericInputRef,
    );

    const authContext = useSelector(
      (state: RootState) => state.yourSlice.authContext,
    );

    const texts = {
      placeholderText: i18n("modules.alarmListNode.placeholderText"),
      nodeIconAltText: i18n("modules.alarmListNode.nodeIconAltText"),
      equal: i18n("modules.alarmListNode.equal"),
      notEqual: i18n("modules.alarmListNode.notEqual"),
      contains: i18n("modules.alarmListNode.contains"),
      notContains: i18n("modules.alarmListNode.notContains"),
      greaterThan: i18n("modules.alarmListNode.greaterThan"),
      lessThan: i18n("modules.alarmListNode.lessThan"),
      greaterThanOrEqual: i18n("modules.alarmListNode.greaterThanOrEqual"),
      lessThanOrEqual: i18n("modules.alarmListNode.lessThanOrEqual"),
      warningTriangleAltText: i18n(
        "modules.alarmListNode.warningTriangleAltText",
      ),
      warningText: i18n("modules.alarmListNode.warningText"),
      placeholderMin: i18n("modules.alarmListNode.placeholderMin"),
      placeholderMax: i18n("modules.alarmListNode.placeholderMax"),
    };

    const leftNumericConditionsArray = [
      /* texts.equal,
  texts.notEqual, */
      texts.lessThan,
      texts.lessThanOrEqual,
    ];

    const rightNumericConditionsArray = [
      /* texts.equal,
  texts.notEqual, */
      texts.greaterThan,
      texts.greaterThanOrEqual,
    ];

    const stringsConditionsArray = [
      texts.equal,
      texts.notEqual,
      texts.contains,
      texts.notContains,
    ];

    const getPosition = (): ToolTipPositionType => {
      if (index === length - 1) {
        return "top";
      } else {
        return "bottom";
      }
    };

    const shouldItBeDisabled = (alarmRow, permissions) => {
      return (
        (alarmRow.getIsOrphanedAlarmNode() &&
          !alarmRow.getAlarm().getComputed()) ||
        !permissions.includes("manage-condition-monitoring")
      );
    };

    return (
      <li className={styles[cssClassName]} key={node.key} draggable={false}>
        <div
          className={cn(styles[`${cssClassName}__outer-wrapper`], {
            [styles[`${cssClassName}__outer-wrapper--active`]]: isAlarmActive,
            [styles[`${cssClassName}__outer-wrapper--selected`]]: node.selected,
            [styles[`${cssClassName}__outer-wrapper--warning`]]:
              alarmRow.getIsOrphanedAlarmNode() &&
              !alarmRow.getAlarm().getComputed(),
            [styles[`${cssClassName}__outer-wrapper--computed`]]: alarmRow
              .getAlarm()
              .getComputed(),
          })}
        >
          <div className={styles[`${cssClassName}__checkbox-info-wrapper`]}>
            <div className={styles[`${cssClassName}__checkbox-wrapper`]}>
              {renderCheckBoxButton()}
            </div>
            <div
              className={styles[`${cssClassName}__icon-info-wrapper`]}
              role="button"
              tabIndex={0}
              onClick={handleClickOnNode}
              onKeyDown={handleClickOnNode}
            >
              <img
                className={styles[`${cssClassName}__icon-wrapper`]}
                src={returnIcon(isAlarmActive)}
                alt={texts.nodeIconAltText}
              />
              <div className={styles[`${cssClassName}__info-wrapper`]}>
                <div
                  className={cn(styles[`${cssClassName}__name`], {
                    [styles[`${cssClassName}__name--active`]]: isAlarmActive,
                  })}
                >
                  {node.name}
                </div>
                <div className={styles[`${cssClassName}__info`]}>
                  {buildAncestorBreadCrumbs()}
                </div>
              </div>
            </div>
            {alarmRow.getIsOrphanedAlarmNode() &&
              !alarmRow.getAlarm().getComputed() && (
                <FlexRowContainer>
                  <ToolTip
                    content={texts.warningText}
                    position={getPosition()}
                    widthHundertPercent={true}
                  >
                    <span
                      className={
                        styles[`${cssClassName}__warning-text-wrapper`]
                      }
                    >
                      <img
                        src={warningTriangle}
                        alt={texts.warningTriangleAltText}
                      />
                      <span className={styles[`${cssClassName}__warning-text`]}>
                        {texts.warningText}
                      </span>
                    </span>
                  </ToolTip>
                </FlexRowContainer>
              )}
          </div>

          {alarmRow.getAlarm().getDataType() === "numeric" ? (
            <FlexRowContainer horizontalGap={12}>
              <RenderOnlyForDevelopers>
                <QuestionMarkWithPopoverForConditionMonitoring />
              </RenderOnlyForDevelopers>
              <select
                disabled={shouldItBeDisabled(alarmRow, authContext.permissions)}
                value={firstOperator}
                onChange={(e) => handleFirstMatchOnClick(e.target.value)}
                className={cn(
                  styles[`${cssClassName}__dropdown`],
                  styles[`${cssClassName}__dropdown--small`],
                  {
                    [styles[`${cssClassName}__input--disabled`]]:
                      shouldItBeDisabled(alarmRow, authContext.permissions),
                  },
                )}
              >
                {leftNumericConditionsArray.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>

              <input
                ref={firstNumericInputRef}
                type="number"
                className={cn(
                  styles[`${cssClassName}__input`],
                  styles[`${cssClassName}__input--small`],
                  {
                    [styles[`${cssClassName}__input--disabled`]]:
                      shouldItBeDisabled(alarmRow, authContext.permissions),
                  },
                )}
                placeholder={texts.placeholderMin}
                value={firstCondition}
                onChange={(e) => setFirstCondition(e.target.value)}
                onBlur={handleFirstConditionOnBlur}
                disabled={shouldItBeDisabled(alarmRow, authContext.permissions)}
              />

              <select
                disabled={shouldItBeDisabled(alarmRow, authContext.permissions)}
                value={secondOperator}
                onChange={(e) => handleSecondMatchOnClick(e.target.value)}
                className={cn(
                  styles[`${cssClassName}__dropdown`],
                  styles[`${cssClassName}__dropdown--small`],
                  {
                    [styles[`${cssClassName}__input--disabled`]]:
                      shouldItBeDisabled(alarmRow, authContext.permissions),
                  },
                )}
              >
                {rightNumericConditionsArray.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>

              <input
                ref={secondNumericInputRef}
                type="number"
                className={cn(
                  styles[`${cssClassName}__input`],
                  styles[`${cssClassName}__input--small`],
                  {
                    [styles[`${cssClassName}__input--disabled`]]:
                      shouldItBeDisabled(alarmRow, authContext.permissions),
                  },
                )}
                placeholder={texts.placeholderMax}
                value={secondCondition}
                onChange={(e) => setSecondCondition(e.target.value)}
                onBlur={handleSecondConditionOnBlur}
                disabled={shouldItBeDisabled(alarmRow, authContext.permissions)}
              />
            </FlexRowContainer>
          ) : (
            <FlexRowContainer horizontalGap={12}>
              <RenderOnlyForDevelopers>
                <QuestionMarkWithPopoverForConditionMonitoring />
              </RenderOnlyForDevelopers>
              <select
                disabled={shouldItBeDisabled(alarmRow, authContext.permissions)}
                value={firstOperator}
                onChange={(e) => handleFirstMatchOnClick(e.target.value)}
                className={cn(styles[`${cssClassName}__dropdown`], {
                  [styles[`${cssClassName}__input--disabled`]]:
                    shouldItBeDisabled(alarmRow, authContext.permissions),
                })}
              >
                {stringsConditionsArray.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className={cn(
                  styles[`${cssClassName}__input`],
                  styles[`${cssClassName}__input--wide`],
                  {
                    [styles[`${cssClassName}__input--disabled`]]:
                      shouldItBeDisabled(alarmRow, authContext.permissions),
                  },
                )}
                value={firstCondition}
                placeholder={texts.placeholderText}
                onChange={(e) => setFirstCondition(e.target.value)}
                onBlur={handleFirstConditionOnBlur}
                disabled={shouldItBeDisabled(alarmRow, authContext.permissions)}
              />
            </FlexRowContainer>
          )}

          <FlexRowContainer>
            <p
              className={cn(styles[`${cssClassName}__unit`], {
                [styles[`${cssClassName}__unit--active`]]: isAlarmActive,
              })}
            >
              {returnUnit(node)}
            </p>
          </FlexRowContainer>
          <FlexRowContainer>
            <ToggleButton
              isActive={isAlarmActive}
              handleClick={() => toggleHandleClick()}
              disabled={
                !authContext?.permissions?.includes(
                  "manage-condition-monitoring",
                )
              }
            />
          </FlexRowContainer>
        </div>
      </li>
    );
  },
);

AlarmListNode.displayName = "AlarmListNode";
